import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import moment from 'moment';
import logotipo from '../../../../../img/Peiix_logotipo_color.png';


// Definición de columnas del reporte
const REPORT_COLUMNS = [
  { key: 'id', header: 'ID' },
  { key: 'fecha', header: 'Fecha' },
  { key: 'hora', header: 'Hora' },
  { key: 'terminal', header: 'Terminal' },
  { key: 'tarjeta_numero', header: 'No. Tarjeta' },
  { key: 'tarjeta_tipo', header: 'Tarjeta' },
  { key: 'tipo', header: 'Tipo' },
  { key: 'subtotal', header: 'Subtotal' },
  { key: 'propina', header: 'Propina' },
  { key: 'transaccion', header: 'Transacción' },
  { key: 'comision', header: 'Comisión' },
  { key: 'iva_comision', header: 'IVA Comisión' },
  { key: 'total', header: 'Total' },
  { key: 'banco', header: 'Banco' },
  { key: 'autorizacion', header: 'No. Autorización' },
  { key: 'transaction_status', header: 'Estado' }
];

/**
 * Obtiene la etiqueta de estado de una transacción
 * @private
 */
const getEstadoEtiqueta = (transaccion) => {
  if (transaccion.transaction_type === 'devolucion') {
    return 'Devolucion';
  }
  
  // Usar transaction_status que es el campo correcto de la API
  const status = transaccion.transaction_status || transaccion.status;
  
  if (status === 'aprobada' || status === 'approved') {
    return 'Aprobada';
  }
  if (status === 'rechazada' || status === 'declined') {
    return 'Rechazada';
  }
  return status || '';
};

/**
 * Formatea transacciones para el reporte (raw data)
 * Devuelve números puros para Excel
 * @private
 */
const formatTransactionsForReport = (transactions) => {
  return transactions.map(tx => {
    const estadoLabel = getEstadoEtiqueta(tx);
    
    // Determinar subtotal y total basado en estructura de datos
    const isCuentita = tx.total !== undefined && tx.total !== null;
    const subtotal = isCuentita ? tx.total_amount : tx.subtotal;
    const totalAmount = isCuentita ? tx.total : tx.total_amount;
    
    return {
      id: tx.transaction_id || '',
      fecha: moment(tx.date).format('DD/MM/YYYY'),
      hora: moment(tx.date).format('HH:mm'),
      terminal: tx.device || '',
      tarjeta_numero: tx.masked_card ? tx.masked_card.slice(6) : '',
      tarjeta_tipo: tx.card_brand || '',
      tipo: tx.card_type === 'DEBIT' ? 'Debito' : 'Credito',
      subtotal: parseFloat(subtotal || 0),
      propina: parseFloat(tx.tip || 0),
      transaccion: parseFloat(totalAmount || 0),
      comision: parseFloat(tx.agreed_commission_value || 0),
      iva_comision: parseFloat(tx.iva_comm_val || 0),
      total: parseFloat(tx.trade_profit || 0),
      banco: tx.bank || '',
      autorizacion: tx.authorization_number || '',
      transaction_status: estadoLabel
    };
  });
};

/**
 * Formatea datos numéricos a string para PDF
 */
const formatForPDF = (data) => {
  return data.map(row => ({
    ...row,
    subtotal: row.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    propina: row.propina.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    transaccion: row.transaccion.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    comision: row.comision.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    iva_comision: row.iva_comision.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    total: row.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  }));
};

/**
 * Genera reporte PDF de transacciones del depósito
 * @param {Array} transactions - Array de transacciones
 * @param {string} depositDate - Fecha del depósito
 * @param {string} cycleRange - Rango del ciclo formateado
 */
export const generateDepositPDF = (transactions, depositDate, cycleRange) => {
  const doc = new jsPDF('landscape');
  const rawData = formatTransactionsForReport(transactions);
  const formattedData = formatForPDF(rawData);
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Agregar Logotipo (A la izquierda)
  const imgWidth = 40;
  const imgHeight = 15;
  doc.addImage(logotipo, 'PNG', 14, 8, imgWidth, imgHeight);

  // Título (Centrado)
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('Reporte de depósitos', pageWidth / 2, 14, { align: 'center' });
  
  // Fecha del reporte (A la derecha)
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(`Fecha del reporte: ${moment(depositDate).format('DD/MM/YYYY')}`, pageWidth - 14, 14, { align: 'right' });
  
  // Ciclo (Centrado)
  const formattedCycle = cycleRange.replace(' - ', ' a ');
  doc.text(`Fecha: ${formattedCycle}`, pageWidth / 2, 22, { align: 'center' });
  
  // Resumen (Transacciones y Total, justificados en el mismo renglón)
  const totalDeposit = rawData.reduce((sum, row) => sum + row.total, 0);
  const formattedCount = transactions.length.toLocaleString('en-US');
  const formattedTotal = totalDeposit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text(`Total de transacciones: ${formattedCount}`, 14, 30);
  doc.text(`Total de depósitos: $${formattedTotal}`, pageWidth - 14, 30, { align: 'right' });
  
  doc.setFontSize(8);
  doc.setFont(undefined, 'italic');
  doc.text("Nota: Este importe puede variar si tiene contracargos en proceso.", pageWidth - 14, 34, { align: 'right' });
  
  // Tabla
  doc.autoTable({
    startY: 38,
    head: [REPORT_COLUMNS.map(col => col.header)],
    body: formattedData.map(row => REPORT_COLUMNS.map(col => row[col.key])),
    styles: { 
      fontSize: 7,
      cellPadding: 1
    },
    headStyles: {
      fillColor: [220, 220, 220],
      textColor: [62, 65, 68],
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245]
    }
  });
  
  // Footer: Fecha de Generación
  const finalY = doc.lastAutoTable.finalY || 40;
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.text(`Archivo generado el: ${moment().format('DD/MM/YYYY HH:mm:ss')}`, 14, finalY + 10);
  
  // Guardar
  const fileName = `DepositoPeiix_${moment(depositDate).format('YYYY-MM-DD')}.pdf`;
  doc.save(fileName);
};

/**
 * Genera reporte Excel de transacciones del depósito
 * @param {Array} transactions - Array de transacciones
 * @param {string} depositDate - Fecha del depósito
 * @param {string} cycleRange - Rango del ciclo formateado
 */
export const generateDepositExcel = (transactions, depositDate, cycleRange) => {
  const formattedData = formatTransactionsForReport(transactions);
  
  // Convertir a formato Excel con headers personalizados
  const excelData = formattedData.map(row => {
    const excelRow = {};
    REPORT_COLUMNS.forEach(col => {
      excelRow[col.header] = row[col.key];
    });
    return excelRow;
  });
  
  // Crear worksheet
  const worksheet = XLSX.utils.json_to_sheet(excelData);
  
  // Crear workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Transacciones');
  
  // Guardar
  const fileName = `DepositoPeiix_${moment(depositDate).format('YYYY-MM-DD')}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};
